import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@/lib/use-server-fn";
import { useEffect, useState } from "react";
import { AppLayout } from "@/components/app-layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Ban, CheckCircle2, ChevronDown, ChevronRight, Circle, Clock, Copy, Download, FileText, Loader2, RotateCcw, XCircle, AlertTriangle } from "lucide-react";

import { getRun, getRunLogs, cancelRun, retryRun } from "@/features/workflows/workflow.functions";
import { aws as awsClient } from "@/aws/client";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { MarkdownView } from "@/components/markdown-view";


export const Route = createFileRoute("/_authenticated/jobs/$runId")({
  remountDeps: ({ params }) => params.runId,
  component: JobDetail,
  head: () => ({
    meta: [
      { title: "Job · AskBell" },
      { name: "description", content: "Workflow run detail, steps, and outputs." },
    ],
  }),
});

const STATUS_TONE: Record<string, string> = {
  queued: "bg-muted text-muted-foreground",
  planning: "bg-ui-info-bg text-ui-info-icon border-ui-info-icon",
  running: "bg-primary/15 text-primary border-primary/30",
  retrying: "bg-ui-warning-bg text-ui-warning-icon border-ui-warning-icon",
  critiquing: "bg-ui-ai-badge-bg text-ui-ai border-ui-ai/30",
  succeeded: "bg-ui-success-bg text-ui-success-icon border-ui-success-icon",
  partial: "bg-ui-warning-bg text-ui-warning-icon border-ui-warning-icon",
  needs_review: "bg-ui-warning-bg text-ui-warning-icon border-ui-warning-icon",
  failed: "bg-destructive/15 text-destructive border-destructive/30",
  stalled: "bg-ui-warning-bg text-ui-warning-icon border-ui-warning-icon",
  cancelled: "bg-muted text-muted-foreground",

};

function StepIcon({ status }: { status: string }) {
  if (status === "succeeded") return <CheckCircle2 className="h-4 w-4 text-ui-success-icon" />;
  if (status === "partial") return <AlertTriangle className="h-4 w-4 text-ui-warning-icon" />;
  if (status === "failed") return <XCircle className="h-4 w-4 text-destructive" />;
  if (status === "running") return <Loader2 className="h-4 w-4 animate-spin text-primary" />;
  if (status === "pending") return <Circle className="h-4 w-4 text-muted-foreground" />;
  return <Clock className="h-4 w-4 text-muted-foreground" />;
}

function JobDetail() {
  const { runId } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const getFn = useServerFn(getRun);
  const logsFn = useServerFn(getRunLogs);
  const cancelFn = useServerFn(cancelRun);
  const retryFn = useServerFn(retryRun);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});


  const q = useQuery({
    queryKey: ["workflow-run", runId],
    queryFn: () => getFn({ data: { id: runId } }),
    // Realtime pushes drive most updates; this is a slow safety net in case a
    // socket drops. Stops entirely once the run reaches a terminal state.
    refetchInterval: (query) => {
      const s = query.state.data?.run?.status;
      return s && ["succeeded", "partial", "failed", "cancelled", "needs_review", "stalled"].includes(s)
        ? false
        : 15000;
    },
  });

  const logsQ = useInfiniteQuery({
    queryKey: ["workflow-run",runId,"logs"],
    enabled: Boolean(q.data?.run),
    initialPageParam: undefined as number|undefined,
    queryFn: ({pageParam}) => logsFn({data:{id:runId,...(pageParam?{after:pageParam}:{})}}),
    getNextPageParam: page => page.next ?? undefined,
    refetchInterval: q.data?.run && ["succeeded","partial","failed","cancelled","needs_review","stalled"].includes(q.data.run.status) ? false : 15000,
  });

  // Live step/log/status pushes straight from Postgres. RLS applies to the
  // replication stream, so users only ever receive their own runs.
  useEffect(() => {
    const channel = awsClient
      .channel(`workflow-run-${runId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "workflow_runs", filter: `id=eq.${runId}` },
        () => qc.invalidateQueries({ queryKey: ["workflow-run", runId] }),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "workflow_run_steps", filter: `run_id=eq.${runId}` },
        () => qc.invalidateQueries({ queryKey: ["workflow-run", runId] }),
      )
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "workflow_run_logs", filter: `run_id=eq.${runId}` },
        () => qc.invalidateQueries({ queryKey: ["workflow-run", runId] }),
      )
      .subscribe();

    return () => {
      void awsClient.removeChannel(channel);
    };
  }, [runId, qc]);


  const cancelMutation = useMutation({
    mutationFn: () => cancelFn({ data: { id: runId } }),
    onSuccess: () => {
      toast.success("Job cancelled");
      qc.invalidateQueries({ queryKey: ["workflow-run", runId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const retryMutation = useMutation({
    mutationFn: () => retryFn({ data: { id: runId } }),
    onSuccess: (res: any) => {
      toast.success(`Retrying from "${String(res?.resumedAt ?? "").replace(/[_-]+/g, " ") || "the failed step"}"`);
      qc.invalidateQueries({ queryKey: ["workflow-run", runId] });
      qc.invalidateQueries({ queryKey: ["workflow-runs"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (q.isLoading) {
    return (
      <AppLayout>
        <div className="p-6 text-ui-base text-muted-foreground">Loading…</div>
      </AppLayout>
    );
  }
  if (q.isError || !q.data) {
    return (
      <AppLayout>
        <div className="mx-auto w-full max-w-5xl p-6 space-y-4">
          <Button variant="ghost" size="sm" onClick={() => navigate({to:"/jobs"})}><ArrowLeft className="h-4 w-4" />Jobs</Button>
          <Card role="alert" className="border-destructive/30 p-5">
            <h1 className="font-bold">Could not load this job</h1>
            <p className="mt-1 text-ui-base text-muted-foreground">{q.error?.message ?? "The job is unavailable."}</p>
            <Button className="mt-4" variant="outline" disabled={q.isFetching} onClick={() => void q.refetch()}>{q.isFetching ? <><Loader2 className="h-4 w-4 animate-spin" />Loading…</> : "Try again"}</Button>
          </Card>
        </div>
      </AppLayout>
    );
  }

  const run = q.data.run;
  const steps = q.data.steps;
  const logs = logsQ.data?.pages.slice().reverse().flatMap(page=>page.logs) ?? [];
  const children = (q.data as any).children ?? [];
  const files = (q.data as any).files ?? [];

  const pickMarkdown = (o: any): string | null => {
    if (!o) return null;
    if (typeof o === "string") return o.trim() ? o : null;
    // Partial outputs carry only a handoff note (internal_notes) — never a result.
    if (o.partial === true) return null;
    const v = o.markdown ?? o.text ?? o.content ?? o.result ?? o.output;
    if (typeof v === "string" && v.trim()) return v;
    return null;
  };
  const pickNotes = (o: any): string | null => {
    const v = o?.internal_notes;
    return typeof v === "string" && v.trim() ? v : null;
  };
  // Prefer the canonical emit_final step, but fall back to the last succeeded
  // step that produced text — AI-drafted templates use their own step names.
  const finalStep =
    steps.find((s: any) => s.name === "emit_final" && pickMarkdown(s.output)) ??
    [...steps]
      .filter((s: any) => s.status === "succeeded" && pickMarkdown(s.output))
      .sort((a: any, b: any) => a.ordinal - b.ordinal)
      .pop();
  const finalMarkdown =
    pickMarkdown(finalStep?.output) ?? pickMarkdown((run.outputs as any)?.final ?? run.outputs);
  const finalCount = (finalStep?.output as any)?.count as number | undefined;
  const criticReport = run.critic_report as { pass?: boolean; gaps?: string[] } | null;
  const canCancel = ["queued", "planning", "running", "retrying", "critiquing"].includes(run.status);
  const canRetry = ["failed", "partial", "cancelled", "needs_review", "stalled"].includes(run.status);
  // How long the job took: frozen at ended_at once it stops.
  const durationLabel = (() => {
    if (!run.started_at) return null;
    const end = run.ended_at && !canCancel ? new Date(run.ended_at).getTime() : Date.now();
    const s = Math.max(0, Math.floor((end - new Date(run.started_at).getTime()) / 1000));
    return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
  })();

  // What the job was actually asked to do, and with what.
  const runInputs = (run.inputs ?? {}) as Record<string, any>;
  const jobPrompt: string =
    typeof runInputs.prompt === "string"
      ? runInputs.prompt
      : typeof runInputs.objective === "string"
        ? runInputs.objective
        : typeof runInputs.source_text === "string"
          ? runInputs.source_text
          : "";
  const jobAttachments: any[] = Array.isArray(runInputs.attachments) ? runInputs.attachments : [];
  const snapshotSteps: any[] = Array.isArray((run.template_snapshot as any)?.steps)
    ? (run.template_snapshot as any).steps
    : [];
  const stepInstructions = snapshotSteps
    .filter((s: any) => typeof s?.instruction === "string" && s.instruction.trim())
    .filter((s: any) => s.instruction.trim() !== jobPrompt.trim())
    .map((s: any) => ({ name: String(s.name ?? "step"), instruction: String(s.instruction) }));


  return (
    <AppLayout>
      <div className="flex-1 min-h-0 overflow-y-auto">
      <div className="mx-auto max-w-5xl p-6 space-y-4">

        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => navigate({ to: "/jobs" })}>
            <ArrowLeft className="h-4 w-4" /> Jobs
          </Button>
        </div>

        <Card className="p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-ui-xl font-bold truncate">
                  {run.title && run.title !== "Workflow run"
                    ? run.title
                    : (run.subagent_name
                        ? `Sub-agent: ${String(run.subagent_name).replace(/[_-]+/g, " ")}`
                        : "Job")}
                </h1>

                <Badge variant="outline" className={STATUS_TONE[run.status] ?? ""}>
                  {run.status}
                </Badge>
              </div>
              <div className="mt-1 text-ui-sm text-muted-foreground">
                {run.started_at
                  ? `Started ${formatDistanceToNow(new Date(run.started_at), { addSuffix: true })}`
                  : `Created ${formatDistanceToNow(new Date(run.created_at), { addSuffix: true })}`}
                {durationLabel ? ` · ${canCancel ? "running for" : "took"} ${durationLabel}` : ""}
                {" · "}{steps.filter((step:any)=>step.status==="succeeded").length} / {steps.length} steps completed
                {run.total_tokens ? ` · ${run.total_tokens.toLocaleString()} tok` : ""}
                {run.retry_count ? ` · ${run.retry_count} critic retries` : ""}
              </div>
              {run.error && (
                <div className="mt-2 text-ui-base text-destructive">{run.error}</div>
              )}
            </div>
            {canCancel && (
              <Button
                variant="outline"
                size="sm"
                disabled={cancelMutation.isPending}
                onClick={() => cancelMutation.mutate()}
              >
                <Ban className="h-4 w-4" /> Cancel
              </Button>
            )}
            {canRetry && (
              <Button
                variant="outline"
                size="sm"
                disabled={retryMutation.isPending}
                onClick={() => retryMutation.mutate()}
                title="Keeps finished steps and their files; restarts from the step that stopped"
              >
                {retryMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}
                Retry from failed step
              </Button>
            )}
          </div>
        </Card>

        {(jobPrompt || jobAttachments.length > 0 || stepInstructions.length > 0) && (
          <Card className="p-4">
            <button
              type="button"
              aria-expanded={!!expanded.__inputs}
              onClick={() => setExpanded((p) => ({ ...p, __inputs: !p.__inputs }))}
              className="flex w-full items-center gap-2 text-left text-ui-base font-bold"
            >
              {expanded.__inputs ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              Inputs
              <span className="text-ui-sm font-normal text-muted-foreground">
                {jobAttachments.length ? `· ${jobAttachments.length} file(s) provided` : ""}
              </span>
            </button>
            {expanded.__inputs && (
              <div className="mt-3 space-y-4 text-ui-base">
                {jobPrompt && (
                  <div>
                    <div className="text-ui-sm uppercase tracking-wide text-muted-foreground mb-1">
                      Request
                    </div>
                    <div className="whitespace-pre-wrap rounded-ui-sm border border-border/60 bg-muted/20 p-3 text-ui-base max-h-72 overflow-y-auto">
                      {jobPrompt}
                    </div>
                  </div>
                )}
                {jobAttachments.length > 0 && (
                  <div>
                    <div className="text-ui-sm uppercase tracking-wide text-muted-foreground mb-1">
                      Files provided
                    </div>
                    <ul className="space-y-1">
                      {jobAttachments.map((f: any, i: number) => (
                        <li key={i} className="flex items-center gap-2 text-ui-base">
                          <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                          <span className="truncate">{f.filename ?? f.name ?? "file"}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {stepInstructions.map((s, i) => (
                  <div key={i}>
                    <div className="text-ui-sm uppercase tracking-wide text-muted-foreground mb-1">
                      Instruction · {s.name.replace(/[_-]/g, " ")}
                    </div>
                    <div className="whitespace-pre-wrap rounded-ui-sm border border-border/60 bg-muted/20 p-3 text-ui-base max-h-72 overflow-y-auto">
                      {s.instruction}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        )}

        <Card className="p-4">
          <div className="text-ui-base font-bold mb-2">Steps</div>
          <div className="space-y-1.5">
            {steps.map((s: any) => {
              const md = pickMarkdown(s.output) ?? pickNotes(s.output);
              const isNotes = !pickMarkdown(s.output) && !!pickNotes(s.output);
              const open = !!expanded[s.id];
              const toolCount = Array.isArray(s.output?.tool_log) ? s.output.tool_log.length : 0;
              return (
                <div key={s.id}>
                  <button
                    type="button"
                    disabled={!md}
                    aria-expanded={open}
                    onClick={() => setExpanded((p) => ({ ...p, [s.id]: !p[s.id] }))}
                    className="flex w-full items-center gap-2 rounded-ui-sm px-1 py-1 text-left text-ui-base disabled:cursor-default hover:bg-muted/40 disabled:hover:bg-transparent"
                  >
                    <StepIcon status={s.status} />
                    <span className="font-mono text-ui-sm w-6 text-muted-foreground">{s.ordinal}</span>
                    <span className="flex-1 truncate">{s.name.replace(/_/g, " ")}</span>
                    <span className="text-ui-sm text-muted-foreground">
                      {toolCount ? `${toolCount} tool calls` : ""}
                      {s.tokens ? ` · ${s.tokens.toLocaleString()} tok` : ""}
                      {s.attempt > 1 ? ` · attempt ${s.attempt}` : ""}
                    </span>
                    {s.error && (
                      <span className="text-ui-sm text-destructive truncate max-w-[240px]">{s.error}</span>
                    )}
                    {md ? (
                      <span className="inline-flex items-center gap-1 text-ui-sm text-primary">
                        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                        {isNotes ? "handoff notes (partial)" : "output"}
                      </span>
                    ) : s.output?.skipped ? (
                      <span className="text-ui-sm text-muted-foreground">skipped — {String(s.output.reason ?? "not needed")}</span>
                    ) : s.status === "succeeded" ? (
                      <span className="text-ui-sm text-muted-foreground">no text output</span>
                    ) : null}
                  </button>
                  {md && open && (
                    <div className="mt-1 mb-2 ml-8 rounded-ui-sm border border-border/60 bg-muted/20 p-3 max-h-96 overflow-y-auto">
                      <MarkdownView files={files.filter((file:any)=>(s.output?.files??[]).some((output:any)=>output.fileId===file.fileId))}>{md}</MarkdownView>
                    </div>
                  )}
                  {(s.input?.files_so_far ?? []).length > 0 && (
                    <div className="mt-1 mb-2 ml-8 text-ui-sm text-muted-foreground">
                      Files written:{" "}
                      {(s.input.files_so_far as any[])
                        .map((f: any) => f?.name)
                        .filter(Boolean)
                        .join(", ")}
                    </div>
                  )}
                  {s.status === "running" && (s.input?.progress_trail ?? []).length > 0 && (
                    <div className="mt-1 mb-2 ml-8 space-y-0.5">
                      {(s.input.progress_trail as string[]).slice(-8).map((line, i) => (
                        <div key={i} className="truncate text-ui-sm text-muted-foreground/80">
                          {line}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>


        {criticReport && (
          <Card className="p-4">
            <div className="flex items-center gap-2 text-ui-base font-bold mb-2">
              {criticReport.pass ? (
                <CheckCircle2 className="h-4 w-4 text-ui-success-icon" />
              ) : (
                <AlertTriangle className="h-4 w-4 text-ui-warning-icon" />
              )}
              Grounding critic {criticReport.pass ? "passed" : "found gaps"}
            </div>
            {!criticReport.pass && criticReport.gaps && criticReport.gaps.length > 0 && (
              <ul className="list-disc pl-5 text-ui-sm text-muted-foreground space-y-1">
                {criticReport.gaps.slice(0, 20).map((g, i) => (
                  <li key={i}>{g}</li>
                ))}
              </ul>
            )}
          </Card>
        )}

        {finalMarkdown && (
          <Card className="p-4">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="text-ui-base font-bold">
                {run.status === "succeeded" ? "Result" : run.status === "partial" ? "Partial result" : canCancel ? "Latest output (in progress)" : `Retained output · ${run.status.replace(/_/g, " ")}`}
                {finalCount ? ` · ${finalCount} items` : ""}
                {finalStep?.name && finalStep.name !== "emit_final" ? (
                  <span className="ml-1 text-ui-sm font-normal text-muted-foreground">
                    (from {finalStep.name.replace(/[_-]/g, " ")})
                  </span>
                ) : null}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(finalMarkdown);
                    toast.success("Result copied");
                  } catch {
                    toast.error("Couldn't copy the result. Check clipboard access and try again.");
                  }
                }}
              >
                <Copy className="h-3.5 w-3.5" /> Copy
              </Button>
            </div>
            <MarkdownView files={files.filter((file:any)=>(finalStep?.output?.files??[]).some((output:any)=>output.fileId===file.fileId))}>{finalMarkdown}</MarkdownView>
          </Card>
        )}

        {children.length > 0 && (
          <Card className="p-4">
            <div className="text-ui-base font-bold mb-2">Sub-agents · {children.length}</div>
            <div className="space-y-1.5">
              {children.map((c: any) => {
                const outs = c.outputs as Record<string,unknown>;
                const cmd = pickMarkdown(outs.agent_work) ?? pickMarkdown(Object.values(outs).map(value=>pickMarkdown(value)).filter(Boolean).join("\n\n"));
                const open = !!expanded[c.id];
                const openIn = !!expanded[`in-${c.id}`];
                const childPrompt =
                  typeof (c.inputs as any)?.prompt === "string" ? (c.inputs as any).prompt : "";
                return (
                  <div key={c.id}>
                    <div className="flex w-full items-center gap-2 rounded-ui-sm px-2 py-1.5 text-ui-base hover:bg-muted/50">
                      <StepIcon status={c.status} />
                      <button
                        type="button"
                        onClick={() => navigate({ to: "/jobs/$runId", params: { runId: c.id } })}
                        className="flex-1 truncate text-left hover:underline"
                      >
                        {(c.subagent_name ?? c.title ?? "sub-agent").replace(/_/g, " ")}
                      </button>
                      {childPrompt && (
                        <button
                          type="button"
                          aria-expanded={openIn}
                          onClick={() => setExpanded((p) => ({ ...p, [`in-${c.id}`]: !p[`in-${c.id}`] }))}
                          className="inline-flex items-center gap-1 text-ui-sm text-muted-foreground hover:text-foreground"
                        >
                          {openIn ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          input
                        </button>
                      )}
                      {cmd && (
                        <button
                          type="button"
                          aria-expanded={open}
                          onClick={() => setExpanded((p) => ({ ...p, [c.id]: !p[c.id] }))}
                          className="inline-flex items-center gap-1 text-ui-sm text-primary"
                        >
                          {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          output
                        </button>
                      )}
                      <Badge variant="outline" className={STATUS_TONE[c.status] ?? ""}>
                        {c.status}
                      </Badge>
                    </div>
                    {childPrompt && openIn && (
                      <div className="mt-1 mb-2 ml-8 whitespace-pre-wrap rounded-ui-sm border border-border/60 bg-muted/10 p-3 text-ui-sm text-muted-foreground max-h-64 overflow-y-auto">
                        {childPrompt}
                      </div>
                    )}
                    {cmd && open && (
                      <div className="mt-1 mb-2 ml-8 rounded-ui-sm border border-border/60 bg-muted/20 p-3 max-h-96 overflow-y-auto">
                        <MarkdownView>{cmd}</MarkdownView>
                      </div>
                    )}

                  </div>
                );
              })}
            </div>
          </Card>
        )}


        {files.length > 0 && (
          <Card className="p-4">
            <div className="text-ui-base font-bold mb-2">Files · {files.length}</div>
            <div className="space-y-1.5">
              {files.map((f: any) => (
                <div key={f.filename} className="flex items-center gap-2 text-ui-base">
                  <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="flex-1 truncate">{f.filename}</span>
                  {f.url ? (
                    <a
                      href={f.url}
                      download={f.filename}
                      className="inline-flex items-center gap-1 text-ui-sm text-primary hover:underline"
                    >
                      <Download className="h-3.5 w-3.5" /> Download
                    </a>
                  ) : (
                    <span className="text-ui-sm text-muted-foreground">unavailable</span>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}


        {(logs.length > 0 || logsQ.isLoading || logsQ.isError) && (
          <Card className="p-4">
            <div className="text-ui-base font-bold mb-2">Activity</div>
            {logsQ.isLoading && <p className="text-ui-base text-muted-foreground">Loading activity…</p>}
            {logsQ.isError && <div role="alert" className="mb-3 flex items-center justify-between gap-3 text-ui-base text-destructive"><span>Activity could not be loaded.</span><Button size="sm" variant="outline" onClick={() => void logsQ.refetch()}>Retry</Button></div>}
            {logsQ.hasNextPage && <Button className="mb-3" size="sm" variant="outline" disabled={logsQ.isFetchingNextPage} onClick={() => void logsQ.fetchNextPage()}>{logsQ.isFetchingNextPage ? "Loading earlier activity…" : "Load earlier activity"}</Button>}
            <div className="space-y-1 max-h-72 overflow-y-auto font-mono text-ui-xs">
              {logs.map((l: any) => (
                <div key={l.id} className="flex gap-2">
                  <span className="text-muted-foreground shrink-0">
                    {new Date(l.created_at).toLocaleTimeString()}
                  </span>
                  <span
                    className={
                      l.level === "error"
                        ? "text-destructive"
                        : l.level === "warn"
                          ? "text-ui-warning-icon"
                          : "text-muted-foreground"
                    }
                  >
                    [{l.phase}]
                  </span>
                  <span className="flex-1">{l.message}</span>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
      </div>

    </AppLayout>
  );
}
